import type { User } from "firebase/auth";
import { resolveGmAccess } from "@/auth/gm-access";
import { ensureGmClaimsSynced, readGmClaims } from "@/auth/sync-gm-claims";
import {
  GM_MONSTER_PLACEHOLDER_IMAGE,
  hydrateGmMonsterCatalog,
  loadGmMonsterCatalog,
  type GmMonsterTemplate,
} from "@/gm/gm-monster-catalog";
import { hydrateGmPartCatalog } from "@/gm/gm-part-catalog";
import { profileToGameArt } from "@/character/spritesheet";
import { loadCharacterProfile } from "@/character/storage";
import { HOME_ASSETS } from "@/home/home-assets";
import type { HomeRoomData } from "@/home/storage";
import {
  mountHomeSceneEditor,
  TOWER_SCENE_EDITOR_FEATURES,
  type HomeSceneEditorHandle,
} from "@/home/mount-home-scene-editor";
import { gmTowerFloorEditorPath } from "@/pages/gm-routes";
import { TOWER_MAX_FLOOR } from "@/tower/tower-floors";
import {
  clampSpawnPerMinute,
  TOWER_DEFAULT_SPAWN_PER_MINUTE,
} from "@/tower/tower-spawn-rate";
import {
  homeRoomToTowerScenePlacements,
  towerFloorToHomeRoom,
} from "@/tower/tower-scene-room-adapter";
import {
  clearTowerFloorConfig,
  getTowerFloorConfig,
  hydrateTowerFloorConfigs,
  isStorageQuotaError,
  saveTowerFloorConfig,
  TOWER_ARENA_COLS,
  TOWER_ARENA_ROWS,
  type TowerFloorSpawnRow,
} from "@/tower/tower-floor-config";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

type DraftRow = TowerFloorSpawnRow & { rowId: string };
type EditorTab = "scene" | "monsters" | "settings";

function clampDraftPowerMultiplier(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(1000, Math.max(0.1, Math.round(value * 100) / 100));
}

function clampDraftCount(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.max(1, Math.floor(value));
}

function monsterThumbSrc(catalog: GmMonsterTemplate[], monsterId: string): string {
  const m = catalog.find((entry) => entry.id === monsterId);
  const url = m?.imageDataUrl;
  return url?.startsWith("data:") ? url : GM_MONSTER_PLACEHOLDER_IMAGE;
}

function monsterMetaLine(catalog: GmMonsterTemplate[], monsterId: string): string {
  const m = catalog.find((entry) => entry.id === monsterId);
  if (!m) return "เลือกมอนสเตอร์จากรายการ";
  return `Lv.${m.monsterLevel} · HP ${m.monsterBaseHp.toLocaleString("en-US")} · ATK ${m.monsterBaseAttack}`;
}

export async function mountGmTowerFloorPage(
  root: HTMLElement,
  user: User,
  floor: number,
): Promise<() => void> {
  root.className = "page-shell page-gm-tower-floor";

  const cachedClaims = await readGmClaims(user);
  const preAccess = resolveGmAccess(user, cachedClaims);
  if (!preAccess.canAccessGmConsole) {
    root.innerHTML = `<main class="auth-card"><h1>ไม่มีสิทธิ์</h1><a href="/gm/tower">กลับ</a></main>`;
    return () => {};
  }

  const profile = loadCharacterProfile(user.uid);
  if (!profile) {
    window.location.replace("/character/create");
    return () => {};
  }

  await ensureGmClaimsSynced(user);
  await hydrateGmMonsterCatalog();
  await hydrateGmPartCatalog();
  await hydrateTowerFloorConfigs();

  let rowSeq = 0;
  const nextRowId = () => {
    rowSeq += 1;
    return `row-${rowSeq}`;
  };

  const initialFloor = Math.min(TOWER_MAX_FLOOR, Math.max(1, Math.floor(floor)));
  const cfg = getTowerFloorConfig(initialFloor);

  let editingFloor = initialFloor;
  let originalFloor = initialFloor;
  let draftEnabled = cfg?.enabled ?? true;
  let draftPowerMultiplier = cfg?.powerMultiplier ?? 1;
  let draftRows: DraftRow[] = (cfg?.spawns ?? []).map((row) => ({
    rowId: nextRowId(),
    monsterId: row.monsterId,
    count: row.count,
    powerMultiplier: row.powerMultiplier ?? 1,
    spawnPerMinute: row.spawnPerMinute ?? TOWER_DEFAULT_SPAWN_PER_MINUTE,
  }));
  let activeTab: EditorTab = "scene";
  let sceneRoom: HomeRoomData = towerFloorToHomeRoom(cfg);
  let editor: HomeSceneEditorHandle | null = null;

  const renderSpawnRowsHtml = () => {
    if (draftRows.length === 0) {
      return `<p class="hint gm-tower-empty">เพิ่มมอนสเตอร์อย่างน้อย 1 รายการ</p>`;
    }
    const catalog = loadGmMonsterCatalog();
    return draftRows
      .map((row) => {
        const options =
          catalog.length === 0
            ? `<option value="">— ยังไม่มีมอนสเตอร์ —</option>`
            : catalog
                .map(
                  (m) =>
                    `<option value="${escapeHtml(m.id)}" ${m.id === row.monsterId ? "selected" : ""}>${escapeHtml(m.name)}</option>`,
                )
                .join("");
        const thumb = monsterThumbSrc(catalog, row.monsterId);
        const meta = monsterMetaLine(catalog, row.monsterId);
        return `
      <article class="gm-tower-spawn-row" data-row-id="${escapeHtml(row.rowId)}">
        <div class="gm-tower-spawn-row-head">
          <div class="gm-tower-spawn-thumb">
            <img class="gm-tower-spawn-thumb-img" src="${escapeHtml(thumb)}" alt="" width="52" height="52" loading="lazy" />
          </div>
          <label class="field gm-tower-spawn-pick">
            <span class="field-label">มอนสเตอร์</span>
            <select class="input gm-tower-monster-select" data-row-id="${escapeHtml(row.rowId)}">${options}</select>
            <span class="hint gm-tower-spawn-meta">${escapeHtml(meta)}</span>
          </label>
          <button type="button" class="btn btn-ghost btn-xs gm-tower-row-remove" data-row-id="${escapeHtml(row.rowId)}" aria-label="ลบแถว">×</button>
        </div>
        <div class="gm-tower-spawn-stats">
          <label class="field gm-tower-spawn-stat">
            <span class="field-label">จำนวน</span>
            <input type="number" class="input gm-tower-count-input" data-row-id="${escapeHtml(row.rowId)}" min="1" step="1" value="${row.count}" />
          </label>
          <label class="field gm-tower-spawn-stat">
            <span class="field-label">พลัง ×</span>
            <input type="number" class="input gm-tower-row-power-input" data-row-id="${escapeHtml(row.rowId)}" min="0.1" max="1000" step="0.1" value="${row.powerMultiplier ?? 1}" />
          </label>
          <label class="field gm-tower-spawn-stat">
            <span class="field-label" title="จำนวนตัวที่เกิดต่อนาที (ช่วงห่างระหว่างตัวในคิว)">ตัว/นาที</span>
            <input type="number" class="input gm-tower-row-spawn-rate-input" data-row-id="${escapeHtml(row.rowId)}" min="0.5" max="600" step="0.5" value="${row.spawnPerMinute ?? TOWER_DEFAULT_SPAWN_PER_MINUTE}" />
          </label>
        </div>
      </article>`;
      })
      .join("");
  };

  const renderAsideHtml = () => {
    const total = draftRows.reduce((sum, r) => sum + r.count, 0);
    const placementCount = editor?.getRoomData().placements.length ?? sceneRoom.placements.length;
    const tabClass = (tab: EditorTab) =>
      tab === activeTab ? "gm-tower-floor-tab gm-tower-floor-tab--active" : "gm-tower-floor-tab";
    return `
      <nav class="gm-tower-floor-tabs" aria-label="แท็บตั้งค่าชั้น">
        <button type="button" class="${tabClass("scene")}" data-editor-tab="scene">ฉาก</button>
        <button type="button" class="${tabClass("monsters")}" data-editor-tab="monsters">มอนสเตอร์</button>
        <button type="button" class="${tabClass("settings")}" data-editor-tab="settings">ตั้งค่า</button>
      </nav>
      <div class="gm-tower-floor-panels">
        <section class="gm-tower-floor-panel" data-editor-panel="scene" ${activeTab === "scene" ? "" : "hidden"}>
          <div class="gm-tower-rows-head">
            <h3>ฉากต่อสู้</h3>
            <p class="hint">${placementCount} ชิ้นวาง · กริด ${TOWER_ARENA_COLS}×${TOWER_ARENA_ROWS}</p>
          </div>
          <p class="hint">ใช้เครื่องมือเดียวกับหน้าบ้าน · แถบชิ้นด้านล่าง · ลากวางบนกริด</p>
        </section>
        <section class="gm-tower-floor-panel" data-editor-panel="monsters" ${activeTab === "monsters" ? "" : "hidden"}>
          <div class="gm-tower-rows-head">
            <h3>รายการเกิด</h3>
            <p class="hint">รวม <strong id="gm-tower-modal-total">${total}</strong> ตัว</p>
          </div>
          <p class="hint gm-tower-spawn-hint">พลัง × ต่อแถว (รวมกับคูณพลังชั้น) · ตัว/นาที = อัตราเกิดของแถวนั้น</p>
          <div class="gm-tower-spawn-list" id="gm-tower-modal-rows">${renderSpawnRowsHtml()}</div>
          <button type="button" class="btn btn-ghost btn-sm gm-tower-add-spawn-btn" id="gm-tower-add-monster-row">+ เพิ่มมอนสเตอร์</button>
        </section>
        <section class="gm-tower-floor-panel" data-editor-panel="settings" ${activeTab === "settings" ? "" : "hidden"}>
          <label class="field">
            <span class="field-label">ชั้นหอคอย (1–${TOWER_MAX_FLOOR})</span>
            <input type="number" id="gm-tower-edit-floor" min="1" max="${TOWER_MAX_FLOOR}" value="${editingFloor}" />
          </label>
          <label class="field gm-tower-power-field">
            <span class="field-label">คูณพลังมอนสเตอร์ (×)</span>
            <input type="number" id="gm-tower-edit-power" min="0.1" max="1000" step="0.1" value="${draftPowerMultiplier}" />
          </label>
          <label class="gm-tower-enabled-field">
            <input type="checkbox" id="gm-tower-edit-enabled" ${draftEnabled ? "checked" : ""} />
            <span>เปิดใช้งานชั้นนี้</span>
          </label>
        </section>
      </div>
    `;
  };

  const renderShell = () => `
    <div class="gm-tower-floor-shell">
      <header class="gm-tower-floor-top">
        <div class="gm-tower-floor-top-main">
          <a class="btn btn-ghost btn-sm" href="/gm/tower">← รายการชั้น</a>
          <h1 class="gm-tower-floor-title">ชั้น ${editingFloor} · แก้ไขฉากต่อสู้</h1>
        </div>
        <div class="gm-tower-floor-top-actions">
          <p class="hint gm-tower-floor-status" id="gm-tower-floor-status" role="status"></p>
          <button type="button" class="btn btn-primary" id="gm-tower-floor-save">บันทึก</button>
        </div>
      </header>
      <div class="gm-tower-floor-layout">
        <div id="gm-tower-scene-editor-host" class="gm-tower-scene-editor-host" aria-label="ฉากต่อสู้"></div>
        <aside class="gm-tower-floor-aside" id="gm-tower-floor-aside">
          ${renderAsideHtml()}
        </aside>
      </div>
    </div>
  `;

  const setStatus = (message: string, isError = false) => {
    const el = root.querySelector<HTMLElement>("#gm-tower-floor-status");
    if (!el) return;
    el.textContent = message;
    el.classList.toggle("gm-tower-floor-status--error", isError);
  };

  const refreshAside = () => {
    const aside = root.querySelector<HTMLElement>("#gm-tower-floor-aside");
    if (!aside) return;
    aside.innerHTML = renderAsideHtml();
  };

  const refreshTitle = () => {
    const title = root.querySelector<HTMLElement>(".gm-tower-floor-title");
    if (title) title.textContent = `ชั้น ${editingFloor} · แก้ไขฉากต่อสู้`;
  };

  root.innerHTML = renderShell();

  const editorHost = root.querySelector<HTMLElement>("#gm-tower-scene-editor-host");
  if (!editorHost) {
    return () => {};
  }

  const gameArt = profileToGameArt(profile);
  const avatarSrc = gameArt.playerIdleTextureUrl || HOME_ASSETS.avatarDefault;

  editor = await mountHomeSceneEditor(root, {
    user,
    profile,
    profileName: profile.name,
    avatarSrc,
    gameArt,
    sceneId: `tower-floor-${editingFloor}`,
    hudSubtitle: `ชั้น ${editingFloor}`,
    pageRootClass:
      "game-app page-my-home page-scene-editor-embedded gm-tower-scene-editor-host",
    shell: "tower",
    features: TOWER_SCENE_EDITOR_FEATURES,
    surfaceElement: editorHost,
    fixedGrid: { cols: TOWER_ARENA_COLS, rows: TOWER_ARENA_ROWS },
    autoEnterEditMode: true,
    loadRoom: async () => sceneRoom,
    saveRoom: async (data) => {
      sceneRoom = data;
    },
  });

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement;

    if (target.id === "gm-tower-floor-save" || target.closest("#gm-tower-floor-save")) {
      void (async () => {
        try {
          editor?.flushSave();
          const room = editor?.getRoomData() ?? sceneRoom;
          const spawns = draftRows
            .filter((r) => r.monsterId)
            .map((r) => ({
              monsterId: r.monsterId,
              count: r.count,
              powerMultiplier: clampDraftPowerMultiplier(r.powerMultiplier ?? 1),
              spawnPerMinute: clampSpawnPerMinute(r.spawnPerMinute ?? TOWER_DEFAULT_SPAWN_PER_MINUTE),
            }));
          const floorInput = root.querySelector<HTMLInputElement>("#gm-tower-edit-floor");
          const enabledInput = root.querySelector<HTMLInputElement>("#gm-tower-edit-enabled");
          const powerInput = root.querySelector<HTMLInputElement>("#gm-tower-edit-power");
          const nextFloor = Math.min(
            TOWER_MAX_FLOOR,
            Math.max(1, Math.floor(Number(floorInput?.value ?? editingFloor))),
          );
          const enabled = enabledInput?.checked ?? draftEnabled;
          const powerMultiplier = clampDraftPowerMultiplier(
            Number(powerInput?.value ?? draftPowerMultiplier),
          );
          const scenePlacements = homeRoomToTowerScenePlacements(room);
          if (originalFloor !== nextFloor) {
            await clearTowerFloorConfig(originalFloor);
          }
          await saveTowerFloorConfig({
            floor: nextFloor,
            spawns,
            enabled,
            powerMultiplier,
            scenePlacements,
            updatedAt: Date.now(),
          });
          sceneRoom = room;
          if (nextFloor !== editingFloor) {
            editingFloor = nextFloor;
            originalFloor = nextFloor;
            history.replaceState(null, "", gmTowerFloorEditorPath(nextFloor));
            refreshTitle();
          }
          setStatus(
            `บันทึกแล้ว · ${spawns.reduce((a, s) => a + s.count, 0)} ตัว · ฉาก ${scenePlacements.length} ชิ้น · พลัง ×${powerMultiplier}`,
          );
          refreshAside();
        } catch (err) {
          const msg = isStorageQuotaError(err)
            ? "พื้นที่เก็บข้อมูลเต็ม"
            : err instanceof Error
              ? err.message
              : "บันทึกไม่สำเร็จ";
          setStatus(msg, true);
        }
      })();
      return;
    }

    const tabBtn = target.closest<HTMLElement>("[data-editor-tab]");
    if (tabBtn?.dataset.editorTab) {
      activeTab = tabBtn.dataset.editorTab as EditorTab;
      refreshAside();
      return;
    }

    if (target.id === "gm-tower-add-monster-row" || target.closest("#gm-tower-add-monster-row")) {
      const catalog = loadGmMonsterCatalog();
      draftRows.push({
        rowId: nextRowId(),
        monsterId: catalog[0]?.id ?? "",
        count: 1,
        powerMultiplier: 1,
        spawnPerMinute: TOWER_DEFAULT_SPAWN_PER_MINUTE,
      });
      activeTab = "monsters";
      refreshAside();
      return;
    }

    const remove = target.closest<HTMLElement>(".gm-tower-row-remove");
    if (remove?.dataset.rowId) {
      draftRows = draftRows.filter((r) => r.rowId !== remove.dataset.rowId);
      refreshAside();
    }
  };

  const syncSpawnRowPreview = (rowId: string, monsterId: string) => {
    const catalog = loadGmMonsterCatalog();
    const rowEl = root.querySelector<HTMLElement>(`.gm-tower-spawn-row[data-row-id="${rowId}"]`);
    if (!rowEl) return;
    const img = rowEl.querySelector<HTMLImageElement>(".gm-tower-spawn-thumb-img");
    if (img) img.src = monsterThumbSrc(catalog, monsterId);
    const meta = rowEl.querySelector<HTMLElement>(".gm-tower-spawn-meta");
    if (meta) meta.textContent = monsterMetaLine(catalog, monsterId);
  };

  const onChange = (event: Event) => {
    const target = event.target as HTMLElement;
    const select = target.closest<HTMLSelectElement>(".gm-tower-monster-select");
    if (select?.dataset.rowId) {
      const row = draftRows.find((r) => r.rowId === select.dataset.rowId);
      if (row) {
        row.monsterId = select.value;
        syncSpawnRowPreview(row.rowId, row.monsterId);
      }
    }
    if (target.id === "gm-tower-edit-enabled") {
      draftEnabled = (target as HTMLInputElement).checked;
    }
  };

  const onInput = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.id === "gm-tower-edit-floor") {
      editingFloor = Math.min(
        TOWER_MAX_FLOOR,
        Math.max(1, Math.floor(Number((target as HTMLInputElement).value)) || 1),
      );
      refreshTitle();
      return;
    }
    if (target.id === "gm-tower-edit-power") {
      draftPowerMultiplier = clampDraftPowerMultiplier(
        Number((target as HTMLInputElement).value) || 1,
      );
      return;
    }
    const countInput = target.closest<HTMLInputElement>(".gm-tower-count-input");
    if (countInput?.dataset.rowId) {
      const row = draftRows.find((r) => r.rowId === countInput.dataset.rowId);
      if (row) {
        row.count = clampDraftCount(Number(countInput.value) || 1);
        countInput.value = String(row.count);
        const total = root.querySelector<HTMLElement>("#gm-tower-modal-total");
        if (total) total.textContent = String(draftRows.reduce((sum, r) => sum + r.count, 0));
      }
    }
    const powerRowInput = target.closest<HTMLInputElement>(".gm-tower-row-power-input");
    if (powerRowInput?.dataset.rowId) {
      const row = draftRows.find((r) => r.rowId === powerRowInput.dataset.rowId);
      if (row) {
        row.powerMultiplier = clampDraftPowerMultiplier(Number(powerRowInput.value) || 1);
        powerRowInput.value = String(row.powerMultiplier);
      }
    }
    const rateInput = target.closest<HTMLInputElement>(".gm-tower-row-spawn-rate-input");
    if (rateInput?.dataset.rowId) {
      const row = draftRows.find((r) => r.rowId === rateInput.dataset.rowId);
      if (row) {
        row.spawnPerMinute = clampSpawnPerMinute(Number(rateInput.value) || TOWER_DEFAULT_SPAWN_PER_MINUTE);
        rateInput.value = String(row.spawnPerMinute);
      }
    }
  };

  root.addEventListener("click", onClick);
  root.addEventListener("change", onChange);
  root.addEventListener("input", onInput);

  return () => {
    root.removeEventListener("click", onClick);
    root.removeEventListener("change", onChange);
    root.removeEventListener("input", onInput);
    editor?.destroy();
    root.innerHTML = "";
  };
}
